import type { JSX, ReactNode } from 'react';

/**
 * Locked 5-band status palette. Every status pill in the admin
 * dashboard must use this primitive so we don't drift across pages.
 *
 * Usage:
 *   <StatusChip band="healthy">Active</StatusChip>
 *   <StatusChip band={bandFromStatus(ticket.status)}>{ticket.status}</StatusChip>
 *   <StatusChip band="critical" pulse>Breach</StatusChip>
 */
export type Band = 'healthy' | 'degraded' | 'warning' | 'critical' | 'neutral';

const BAND_TONE: Record<Band, { fg: string; bg: string; border: string; dot: string }> = {
  healthy: {
    fg: 'var(--success)',
    bg: 'var(--success-soft)',
    border: 'rgba(16, 185, 129, 0.3)',
    dot: 'var(--success)',
  },
  degraded: {
    fg: 'var(--warning)',
    bg: 'var(--warning-soft)',
    border: 'rgba(245, 158, 11, 0.3)',
    dot: 'var(--warning)',
  },
  warning: {
    fg: '#f59e0b',
    bg: 'rgba(245, 158, 11, 0.10)',
    border: 'rgba(245, 158, 11, 0.3)',
    dot: '#f59e0b',
  },
  critical: {
    fg: 'var(--danger)',
    bg: 'var(--danger-soft)',
    border: 'rgba(239, 68, 68, 0.3)',
    dot: 'var(--danger)',
  },
  neutral: {
    fg: 'var(--text-muted)',
    bg: 'rgba(148, 163, 184, 0.1)',
    border: 'rgba(148, 163, 184, 0.3)',
    dot: 'var(--text-muted)',
  },
};

export function StatusChip({
  band,
  children,
  size = 'sm',
  pulse = false,
  noDot = false,
  className,
}: {
  band: Band;
  children: ReactNode;
  size?: 'sm' | 'md';
  pulse?: boolean;
  noDot?: boolean;
  className?: string;
}): JSX.Element {
  const tone = BAND_TONE[band];
  const padding = size === 'md' ? '3px 9px' : '2px 7px';
  const fontSize = size === 'md' ? '0.75rem' : '0.6875rem';
  return (
    <span
      className={className}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        padding,
        borderRadius: 999,
        fontSize,
        fontWeight: 500,
        color: tone.fg,
        background: tone.bg,
        border: `1px solid ${tone.border}`,
        textTransform: 'capitalize',
        lineHeight: 1.2,
        whiteSpace: 'nowrap',
      }}
    >
      {!noDot && (
        <span
          aria-hidden
          style={{
            position: 'relative',
            width: 6,
            height: 6,
            borderRadius: 999,
            background: tone.dot,
            flex: 'none',
          }}
        >
          {pulse && (
            <span
              aria-hidden
              style={{
                position: 'absolute',
                inset: -3,
                borderRadius: 999,
                background: tone.dot,
                opacity: 0.4,
                animation: 'admin-pulse 1.4s ease-out infinite',
              }}
            />
          )}
        </span>
      )}
      {children}
    </span>
  );
}

/**
 * Maps arbitrary status strings (running, failed, breach, in_progress,
 * etc.) onto the locked 5-band palette so callers don't have to roll
 * their own per-domain mappings.
 */
export function bandFromStatus(status: string | null | undefined): Band {
  if (!status) return 'neutral';
  const s = status.toLowerCase().replace(/[\s_-]/g, '');
  if (/^(active|healthy|verified|fixed|paid|ok|resolved|online|completed|live|running|done)$/.test(s)) {
    return 'healthy';
  }
  if (/^(degraded|investigating|inprogress|pending|warning|expiringsoon|trial|trialing|stale)$/.test(s)) {
    return 'degraded';
  }
  if (/^(waiting|onhold|paused|new|open)$/.test(s)) {
    return 'warning';
  }
  if (/^(failed|breach|critical|expired|revoked|wontfix|cancelled|canceled|offline|incomplete|suspended|quarantined)$/.test(s)) {
    return 'critical';
  }
  return 'neutral';
}
